import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { authConfig } from '../config/auth.config';
import keycloak from '../keycloak-init';

@Injectable()
export class AuthHttpInterceptor implements HttpInterceptor {

  private PDF_SERVICE_URL = environment.pdfServiceBaseurl;

  constructor(
    private router: Router
  ) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Iconos SVG y estáticos: no llevan Bearer ni deben loguearse como errores de API.
    // Si un mat-icon se destruye (p. ej. *ngIf de loading), Angular cancela el XHR (status 0)
    // y sin este bypass el interceptor lo reporta como HttpErrorResponse.
    if (this.isStaticAsset(req.url)) {
      return next.handle(req);
    }

    // Usar token de Keycloak si está disponible, sino usar token del localStorage o el token estático para pruebas
    let token = keycloak.token || localStorage.getItem('token') || authConfig.token;
    const version_patch: string = authConfig.version_patch;
    const appId: string = authConfig.appId;

    // Excepción para Cloudinary
    if (req.url.includes('https://api.cloudinary.com')) {
      return next.handle(req).pipe(
        catchError(this.interceptError)
      );
    }

    // Excepción para PDF Service
    if (req.url.includes(this.PDF_SERVICE_URL)) {
      return next.handle(req);  // No se aplican headers o modificaciones a esta petición
    }

    // Si Keycloak está autenticado pero el token está a punto de expirar, actualizarlo
    if (keycloak.authenticated && keycloak.token) {
      // Verificar si el token necesita ser actualizado (menos de 30 segundos antes de expirar)
      keycloak.updateToken(30).catch(() => {
        console.warn('No se pudo actualizar el token de Keycloak');
      });
      token = keycloak.token;
    }

    let headers = new HttpHeaders({
      version_patch: version_patch,
      appId: appId,
    });

    if (token == null || token == undefined || token == '') {
      return next.handle(req.clone({ headers }));
    }

    headers = new HttpHeaders({
      version_patch: version_patch,
      appId: appId,
      Authorization: `Bearer ${token}`,
    });

    const reqCopy = req.clone({
      headers
    });

    return next.handle(reqCopy).pipe(
      catchError(this.interceptError)
    );
  }

  interceptError(err: HttpErrorResponse): Observable<HttpEvent<any>> {
    console.error(err);
    if (err.status === 401) {
      // Solo hacer logout si Keycloak no está autenticado
      // Si Keycloak está autenticado pero recibimos un 401, puede ser un problema de token
      // o el backend no reconoce el token de Keycloak, pero no debemos hacer logout
      if (!keycloak.authenticated) {
        console.error('Acceso restringido - No autenticado en Keycloak');

        // Limpiar localStorage
        localStorage.removeItem('token');
        localStorage.removeItem('sessionId');
        localStorage.removeItem('name');
        localStorage.removeItem('surname');
        localStorage.removeItem('email');
        localStorage.removeItem('photo');
        localStorage.removeItem('menuArbolSwiss');

        // Hacer logout de Keycloak (esto redirigirá automáticamente al login de Keycloak)
        keycloak.logout();
      } else {
        // Si Keycloak está autenticado pero recibimos un 401, puede ser un problema de token
        // Intentar actualizar el token de Keycloak
        console.warn('Error 401 pero Keycloak está autenticado. Intentando actualizar token...');
        
        keycloak.updateToken(30).then((refreshed) => {
          if (refreshed) {
            console.log('Token actualizado exitosamente');
          } else {
            console.warn('Token no actualizado, puede que aún sea válido');
          }
        }).catch(() => {
          console.error('Error al actualizar token de Keycloak');
          // Solo hacer logout si realmente no podemos actualizar el token
          keycloak.logout();
        });
      }

    } else if (err.status == 400) {
      console.error('Petición invalida');
    }

    return throwError(err.error);
  }

  private isStaticAsset(url: string): boolean {
    const path = (url || '').split('?')[0];
    // Solo estáticos del panel. No matchear cualquier .svg: un endpoint autenticado
    // podría terminar en .svg y se saltaría el Bearer.
    return path.includes('/assets/') || path.startsWith('assets/');
  }
}
